import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, Chip, Button, CircularProgress, Checkbox, Dialog, Select, MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Close, Search, ArrowBack, Print, GroupAdd, Undo, CurrencyExchange, HourglassTop, Block, SearchOff, ManageSearch, Key, ErrorOutline,
  PersonAdd, Delete, Verified, AdminPanelSettings, Sms, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import {
  useGetGiftVoucherSalesQuery, useGetGiftVoucherSaleQuery, useAssignGiftVoucherRecipientsMutation,
  useRequestGiftVoucherApprovalMutation, useCancelGiftVoucherSaleMutation,
} from '../../../services/giftVoucherApi';
import { useVoucherApproval } from '../../../hooks/useVoucherApproval';
import { useThermalReceiptPrint } from '../../../hooks/useThermalReceiptPrint';
import { useGetReceiptTemplateQuery } from '../../../services/receiptTemplateApi';
import { mapGiftVoucherSaleToReceipt } from '../receipt/mapGiftVoucherSaleToReceipt';
import { useToast } from '../../common/ToastProvider';
import {
  GV, EMAIL_RE, inr, inr2, fmtDateTime, SALE_STATUS, VOUCHER_STATUS, DELIVERY_STATUS, DELIVERY_KIND, saleStatusKey,
} from './gvUtils';

const FILTERS = [
  { v: '', l: 'All' }, { v: 'paid', l: 'Paid' }, { v: 'awaiting_issue', l: 'Awaiting issue' },
  { v: 'partially_cancelled', l: 'Part. cancelled' }, { v: 'cancelled', l: 'Cancelled' },
];

function StatusChip({ map, value }) {
  const s = map[value] || { label: value, bg: '#F1F5F9', color: '#64748B' };
  return <Chip size="small" label={s.label} sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: s.bg, color: s.color, borderRadius: '6px', flexShrink: 0 }} />;
}

function Note({ tone, icon, children }) {
  const t = { warn: ['#FFFBEB', '#FDE68A', '#92400E'], err: ['#FEF2F2', '#FECACA', '#DC2626'], ok: ['#F0FDF4', '#BBF7D0', '#166534'], info: ['#EEF2FF', '#C7D2FE', '#3730A3'] }[tone];
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.4, py: 1.1, borderRadius: '8px', bgcolor: t[0], border: `1px solid ${t[1]}`, color: t[2], fontSize: '0.72rem', lineHeight: 1.45 }}>
      {React.cloneElement(icon, { sx: { fontSize: 17, mt: '1px', flexShrink: 0 } })}<Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

const drawerPaper = (wide) => ({ sx: { width: { xs: '100vw', sm: wide ? 480 : 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } });

// ── Assign people (post-purchase) ───────────────────────────────────────────
function AssignContent({ sale, onClose, isMobile }) {
  const showToast = useToast();
  const [assign, { isLoading }] = useAssignGiftVoucherRecipientsMutation();
  const vouchers = sale?.vouchers || [];
  const amounts = [...new Set(vouchers.map((v) => Number(v.face_value)))].sort((a, b) => a - b);
  const available = vouchers.filter((v) => v.status === 'active' && !v.ever_redeemed && !v.recipient_email && !v.recipient_name)
    .reduce((acc, v) => { const k = Number(v.face_value); acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const [rows, setRows] = useState(() => [{ name: '', email: '', phone: '', amount: amounts[0] || '' }]);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState({});
  const [shortfall, setShortfall] = useState(null);

  const setRow = (i, k, v) => { setRows((p) => p.map((r, ix) => (ix === i ? { ...r, [k]: v } : r))); setShortfall(null); };
  const submit = async () => {
    const e = {};
    rows.forEach((r, i) => { if (!EMAIL_RE.test(r.email.trim())) e[i] = 'Email is missing or invalid'; });
    if (!reason.trim()) e.reason = 'A reason is required';
    setErr(e);
    if (Object.keys(e).length) return;
    try {
      const res = await assign({ id: sale.id, body: { reason: reason.trim(), recipients: rows.map((r) => ({ name: r.name.trim(), email: r.email.trim(), phone: r.phone.trim(), amount: Number(r.amount) })) } }).unwrap();
      const n = res?.data?.assigned?.length ?? rows.length;
      showToast(`${n} ${n === 1 ? 'person' : 'people'} assigned — new PINs emailed`, 'success');
      onClose();
    } catch (er) {
      setShortfall(er?.data?.details?.shortfall || null);
      showToast(er?.data?.error || 'Could not assign people', 'error');
    }
  };

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#EEF2FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><GroupAdd sx={{ fontSize: 20 }} /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1E293B' }}>Assign people</Typography>
          <Typography noWrap sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>{sale?.number} · each person gets an unused voucher of their amount</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isLoading} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {Object.keys(available).length ? Object.entries(available).map(([a, n]) => (
            <Chip key={a} size="small" label={`${n} × ${inr(a)} free to assign`} sx={{ bgcolor: '#FFF', border: '1px solid #E2E8F0', fontWeight: 700, fontSize: '0.66rem' }} />
          )) : <Chip size="small" label="No unassigned vouchers left" sx={{ bgcolor: '#FFF', border: '1px solid #E2E8F0', fontWeight: 700, fontSize: '0.66rem' }} />}
        </Box>
        {shortfall && (
          <>
            <Note tone="err" icon={<ErrorOutline />}><b>Not enough unassigned vouchers of some amounts — nothing was changed.</b></Note>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
              <Box component="thead"><Box component="tr">{['AMOUNT', 'NEEDED', 'AVAILABLE'].map((h) => <Box component="th" key={h} sx={{ bgcolor: '#F8FAFC', fontSize: '0.62rem', fontWeight: 800, color: '#64748B', textAlign: 'left', px: 1.25, py: 1 }}>{h}</Box>)}</Box></Box>
              <Box component="tbody">{shortfall.map((s) => (
                <Box component="tr" key={s.amount}>
                  <Box component="td" sx={{ px: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9', fontWeight: 800 }}>{inr(s.amount)}</Box>
                  <Box component="td" sx={{ px: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9' }}>{s.requested}</Box>
                  <Box component="td" sx={{ px: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9', color: '#DC2626', fontWeight: 800 }}>{s.available}</Box>
                </Box>
              ))}</Box>
            </Box>
          </>
        )}
        {rows.map((r, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1.3fr 104px 32px', gap: 1, alignItems: 'start', ...(isMobile ? { border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.25 } : {}) }}>
            <TextField size="small" placeholder="Name" value={r.name} onChange={(e) => setRow(i, 'name', e.target.value)} sx={{ gridColumn: isMobile ? '1 / -1' : 'auto', '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' } }} inputProps={{ 'aria-label': `Name ${i + 1}` }} />
            <TextField size="small" placeholder="Email *" value={r.email} onChange={(e) => setRow(i, 'email', e.target.value)} error={!!err[i]} helperText={err[i]} sx={{ gridColumn: isMobile ? '1 / -1' : 'auto', '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' } }} inputProps={{ 'aria-label': `Email ${i + 1}` }} />
            <Select size="small" value={r.amount} onChange={(e) => setRow(i, 'amount', e.target.value)} sx={{ borderRadius: '8px', fontSize: '0.8rem' }} inputProps={{ 'aria-label': `Amount ${i + 1}` }}>
              {amounts.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: '0.8rem' }}>{inr(a)}</MenuItem>)}
            </Select>
            <IconButton size="small" disabled={rows.length === 1} onClick={() => setRows((p) => p.filter((_, ix) => ix !== i))} aria-label="Remove person"
              sx={{ color: '#94A3B8', justifySelf: 'end', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}><Delete sx={{ fontSize: 17 }} /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<PersonAdd sx={{ fontSize: 16 }} />} onClick={() => setRows((p) => [...p, { name: '', email: '', phone: '', amount: amounts[0] || '' }])}
          sx={{ alignSelf: 'flex-start', borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: '#4F46E5', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE' }}>
          Add person
        </Button>
        <TextField size="small" label="Reason *" value={reason} onChange={(e) => setReason(e.target.value)} error={!!err.reason} helperText={err.reason} placeholder="e.g. HR shared the employee list" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
        <Note tone="warn" icon={<Key />}><b>New PINs will be emailed to each person.</b> The PINs {sale?.buyer_name?.split(' ')[0] || 'the buyer'} already has for these vouchers will stop working.</Note>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px solid #F1F5F9', '& .MuiButton-root': { minHeight: 38, ...(isMobile ? { flex: 1 } : {}) } }}>
        <Button onClick={onClose} disabled={isLoading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, border: '1px solid #E2E8F0', color: '#64748B' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={isLoading || !Object.keys(available).length}
          sx={{ bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>
          {isLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Assign ${rows.length} ${rows.length === 1 ? 'person' : 'people'}`}
        </Button>
      </Box>
    </>
  );
}

function AssignDialog({ open, sale, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px' } }}>
      {open && sale && <AssignContent sale={sale} onClose={onClose} isMobile={isMobile} />}
    </Dialog>
  );
}

// ── Cancel vouchers / refund a pending sale (needs manager approval) ────────
function CancelContent({ sale, voucherIds, whole, onClose, onDone, isMobile }) {
  const showToast = useToast();
  const [reason, setReason] = useState(whole && sale?.issue_rejected_at ? 'Rejected by admin' : '');
  const [reasonErr, setReasonErr] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [otp, setOtp] = useState('');
  const [requestApproval, { isLoading: isRequesting }] = useRequestGiftVoucherApprovalMutation();
  const [cancel, { isLoading: isCancelling }] = useCancelGiftVoucherSaleMutation();
  const approval = useVoucherApproval(requestId);


  const vouchers = (sale?.vouchers || []).filter((v) => voucherIds.includes(v.id));
  const refund = whole ? Number(sale?.amount_paid || 0) : vouchers.reduce((a, v) => a + Number(v.amount_paid || 0), 0);
  const modes = (sale?.payments || []).filter((p) => Number(p.amount) > 0).map((p) => p.payment_mode).join(' + ');

  const request = async () => {
    if (!reason.trim()) { setReasonErr(true); return; }
    try {
      const res = await requestApproval({ id: sale.id, kind: 'voucher_sale_cancellation', reason: reason.trim(), ...(whole ? {} : { voucher_ids: voucherIds }) }).unwrap();
      setRequestId(res?.data?.discount_approval_request?.id);
      showToast('Approval requested', 'info');
    } catch (er) { showToast(er?.data?.error || 'Could not request approval', 'error'); }
  };
  const doCancel = async () => {
    try {
      const res = await cancel({ id: sale.id, reason: reason.trim(), approval_request_id: requestId, ...(whole ? {} : { voucher_ids: voucherIds }) }).unwrap();
      const c = res?.data?.cancellation;
      showToast(whole ? `Sale refunded · ${c?.number}` : `${voucherIds.length} voucher${voucherIds.length !== 1 ? 's' : ''} cancelled · ${inr(c?.refund_total)} refunded`, 'success');
      onDone();
    } catch (er) { showToast(er?.data?.error || 'Cancellation failed', 'error'); }
  };

  const step = !requestId ? 'form' : approval.status === 'approved' ? 'approved' : approval.status === 'pending' ? 'pending' : 'rejected';
  const btn = { borderRadius: '8px', textTransform: 'none', fontWeight: 700, minHeight: 38, ...(isMobile ? { flex: 1 } : {}) };

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#FEF2F2', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{whole ? <CurrencyExchange sx={{ fontSize: 20 }} /> : <Undo sx={{ fontSize: 20 }} />}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1E293B' }}>{whole ? 'Refund whole sale' : `Cancel ${vouchers.length} voucher${vouchers.length !== 1 ? 's' : ''}`}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>Needs a manager’s approval</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isCancelling} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.5, display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: '6px 14px', fontSize: '0.75rem' }}>
          <Typography sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>Sale</Typography><Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }}>{sale?.number}</Typography>
          <Typography sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>Vouchers</Typography>
          <Box>{whole ? 'None issued yet (pending sale)' : vouchers.map((v) => <Box key={v.id} sx={{ fontSize: '0.75rem' }}><Box component="span" sx={{ fontFamily: 'monospace' }}>{v.masked_code.slice(-9)}</Box> · paid {inr(v.amount_paid)}</Box>)}</Box>
          <Typography sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>Refund</Typography><Typography sx={{ fontWeight: 900, color: '#4F46E5', fontSize: '0.8rem' }}>{inr2(refund)} → {modes || 'original modes'}</Typography>
        </Box>
        {step === 'form' && (
          <>
            <TextField size="small" multiline minRows={2} label="Reason *" value={reason} onChange={(e) => { setReason(e.target.value); setReasonErr(false); }} error={reasonErr} helperText={reasonErr ? 'A reason is required' : ''}
              placeholder="e.g. Buyer changed their mind" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
            <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8' }}>Refunds go back to the original payment modes.{whole ? '' : ' Only vouchers that were never used can be cancelled, within the refund window.'}</Typography>
          </>
        )}
        {step === 'pending' && (
          <Note tone="warn" icon={<HourglassTop />}>
            <b>Waiting for manager approval</b><Box sx={{ fontSize: '0.66rem', mt: 0.25 }}>Request #{requestId} · approve on the manager’s screen or enter the OTP they received.</Box>
            {approval.cycle ? (
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                <TextField size="small" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP" inputProps={{ inputMode: 'numeric', 'aria-label': 'Manager OTP' }}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#FFF', borderRadius: '6px' } }} />
                <Button size="small" disabled={otp.length !== 6 || approval.isVerifying} onClick={() => approval.verifyOtpCode(otp)}
                  sx={{ height: 32, borderRadius: '6px', textTransform: 'none', fontWeight: 700, bgcolor: '#4F46E5', color: '#FFF', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0' } }}>Verify</Button>
              </Box>
            ) : (
              <Button size="small" startIcon={<Sms sx={{ fontSize: '14px !important' }} />} onClick={approval.startOtpCycle} disabled={approval.isStarting}
                sx={{ mt: 0.75, height: 26, px: 1, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.66rem', color: '#4F46E5', bgcolor: '#FFF', border: '1px solid #C7D2FE' }}>
                Use manager OTP
              </Button>
            )}
          </Note>
        )}
        {step === 'approved' && <Note tone="ok" icon={<Verified />}><b>Approved{approval.approvedBy ? ` by ${approval.approvedBy}` : ''}</b> · request #{requestId}. This approval is for exactly this {whole ? 'sale' : 'set of vouchers'} and can be used once.</Note>}
        {step === 'rejected' && <Note tone="err" icon={<Block />}><b>The manager {approval.status === 'rejected' ? 'rejected' : 'did not approve'} this request.</b> Nothing was refunded.</Note>}
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px solid #F1F5F9' }}>
        <Button onClick={onClose} disabled={isCancelling} sx={{ ...btn, border: '1px solid #E2E8F0', color: '#64748B' }}>{step === 'pending' ? 'Close' : step === 'form' ? 'Keep' : 'Not now'}</Button>
        {step === 'form' && (
          <Button variant="contained" disableElevation onClick={request} disabled={isRequesting} startIcon={<AdminPanelSettings />} sx={{ ...btn, bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
            {isRequesting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Request approval'}
          </Button>
        )}
        {step === 'approved' && (
          <Button variant="contained" disableElevation onClick={doCancel} disabled={isCancelling} sx={{ ...btn, bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}>
            {isCancelling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `${whole ? 'Refund' : 'Cancel & refund'} ${inr(refund)}`}
          </Button>
        )}
      </Box>
    </>
  );
}

function CancelDialog({ open, sale, voucherIds, whole, onClose, onDone }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px' } }}>
      {open && sale && <CancelContent sale={sale} voucherIds={voucherIds} whole={whole} onClose={onClose} onDone={onDone} isMobile={isMobile} />}
    </Dialog>
  );
}

// ── Sale detail drawer ──────────────────────────────────────────────────────
function SaleDetailContent({ saleId, onBack, onCloseAll }) {
  const { data, isFetching, isError, refetch } = useGetGiftVoucherSaleQuery(saleId, { skip: !saleId });
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const { printReceipt } = useThermalReceiptPrint();
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(null);
  const sale = data?.data?.gift_voucher_sale;


  const key = saleStatusKey(sale);
  const canCancel = sale?.status === 'paid' || sale?.status === 'partially_cancelled';
  const vouchers = sale?.vouchers || [];
  const refund = vouchers.filter((v) => selected.includes(v.id)).reduce((a, v) => a + Number(v.amount_paid || 0), 0);
  const deliveries = sale?.deliveries || [];

  // Reprint: the same thermal receipt the counter printed (no cash change on a reprint).
  const print = () => printReceipt(mapGiftVoucherSaleToReceipt(sale, receiptTemplate));
  const label = (d) => (d.kind === 'recipient' ? vouchers.find((v) => v.id === d.gift_voucher_id)?.recipient_name || 'Recipient' : DELIVERY_KIND[d.kind] || d.kind);
  const paidBy = (sale?.payments || []).filter((p) => Number(p.amount) > 0).map((p) => `${p.payment_mode}${p.reference_number ? ` · ${p.reference_number}` : ''}`).join(' + ');

  return (
    <>
      <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: '#64748B', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.55 }} aria-label="Back"><ArrowBack sx={{ fontSize: 18 }} /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.95rem', fontFamily: 'monospace' }}>{sale?.number || '…'}</Typography>
            {sale && <StatusChip map={SALE_STATUS} value={key} />}
          </Box>
          {sale && <Typography noWrap sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>{fmtDateTime(sale.paid_at)} · {sale.pos_terminal_name} · {sale.cashier_name}</Typography>}
        </Box>
        <IconButton size="small" onClick={onCloseAll} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {isFetching && !sale ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} sx={{ color: '#94A3B8' }} /></Box>
          : isError || !sale ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: 1 }}>Couldn’t load this sale.</Typography>
              <Button onClick={refetch} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>
            </Box>
          ) : (
            <>
              {sale.status === 'awaiting_issue' && !sale.issue_rejected_at && (
                <Note tone="warn" icon={<HourglassTop />}><b>Waiting for an admin to generate the vouchers.</b><Box sx={{ fontSize: '0.68rem', mt: 0.25 }}>Paid {inr(sale.amount_paid)} by {paidBy}. Nothing can be redeemed or assigned until then.</Box></Note>
              )}
              {sale.issue_rejected_at && sale.status === 'awaiting_issue' && (
                <Note tone="err" icon={<Block />}><b>Rejected by {sale.issue_rejected_by_name || 'an admin'}</b> · {fmtDateTime(sale.issue_rejected_at)}<Box sx={{ mt: 0.25 }}>“{sale.issue_rejection_reason}”. Refund the buyer: the whole {inr(sale.amount_paid)} goes back by {paidBy}.</Box></Note>
              )}
              {sale.status === 'cancelled' && <Note tone="err" icon={<Undo />}><b>Cancelled and refunded.</b></Note>}

              <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.5, display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: '6px 14px' }}>
                {[['Buyer', sale.buyer_name], ['Email', sale.buyer_email], ['Delivery', sale.delivery_mode === 'individual' ? 'Each person' : 'All to buyer'],
                  ['Lines', (sale.lines || []).map((l) => `${l.gift_voucher_type_name} ${inr(l.face_value)} × ${l.quantity}`).join('\n')],
                  ['Paid', `${inr2(sale.amount_paid)} · ${paidBy}${Number(sale.discount_total) > 0 ? ` (face ${inr(sale.face_value_total)}, −${inr(sale.discount_total)})` : ''}`]].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>{k}</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{v}</Typography>
                  </React.Fragment>
                ))}
              </Box>

              {vouchers.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem' }}>VOUCHERS ({vouchers.length})</Typography>
                    {canCancel && <Typography sx={{ fontSize: '0.64rem', color: '#94A3B8' }}>Tick unused vouchers to cancel</Typography>}
                  </Box>
                  {vouchers.map((v) => {
                    const disabled = !canCancel || v.ever_redeemed || v.status !== 'active';
                    const why = v.ever_redeemed ? 'Used — can’t be cancelled' : v.status !== 'active' ? VOUCHER_STATUS[v.status]?.label : '';
                    return (
                      <Box key={v.id} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.9, borderBottom: '1px solid #F1F5F9', cursor: disabled ? 'default' : 'pointer', '&:last-child': { borderBottom: 'none' } }}>
                        <Checkbox size="small" disabled={disabled} checked={selected.includes(v.id)} sx={{ p: 0.25 }}
                          onChange={(e) => setSelected((p) => (e.target.checked ? [...p, v.id] : p.filter((x) => x !== v.id)))} inputProps={{ 'aria-label': `Select ${v.masked_code}` }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }}>{v.masked_code}</Typography>
                          <Typography noWrap sx={{ fontSize: '0.66rem', color: '#94A3B8' }}>{v.recipient_name || 'Not assigned'}{why ? ` · ${why}` : ''}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.75rem' }}>{inr(v.balance)}</Typography>
                          <Typography sx={{ fontSize: '0.64rem', color: '#94A3B8' }}>of {inr(v.face_value)}</Typography>
                        </Box>
                        <StatusChip map={VOUCHER_STATUS} value={v.status} />
                      </Box>
                    );
                  })}
                </Box>
              )}

              {deliveries.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem', mb: 0.75 }}>EMAILS</Typography>
                  <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                    {deliveries.map((d) => (
                      <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid #F1F5F9', '&:last-child': { borderBottom: 'none' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{label(d)}</Typography>
                          <Typography noWrap sx={{ fontSize: '0.7rem', color: '#64748B' }}>{d.to_email}</Typography>
                        </Box>
                        <StatusChip map={DELIVERY_STATUS} value={d.status} />
                      </Box>
                    ))}
                  </Box>
                  {deliveries.some((d) => d.status === 'failed') && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.75 }}>A failed email can be resent from the admin panel.</Typography>}
                </Box>
              )}
            </>
          )}
      </Box>

      {sale && (
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #F1F5F9', display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0, '& .MuiButton-root': { borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', minHeight: 36 } }}>
          <Button onClick={print} disabled={!sale.number} startIcon={<Print />} sx={{ border: '1px solid #E2E8F0', color: '#64748B' }}>Reprint</Button>
          {canCancel && <Button onClick={() => setDialog('assign')} startIcon={<GroupAdd />} sx={{ border: '1px solid #E2E8F0', color: '#64748B' }}>Assign people</Button>}
          {canCancel && (
            <Button onClick={() => setDialog('cancel')} disabled={!selected.length} startIcon={<Undo />} sx={{ ml: 'auto', border: '1px solid #EF4444', color: '#EF4444', '&.Mui-disabled': { borderColor: '#F1F5F9' } }}>
              Cancel {selected.length || ''}{selected.length ? ` · ${inr(refund)}` : ''}
            </Button>
          )}
          {sale.status === 'awaiting_issue' && (
            <Button onClick={() => setDialog('refund')} startIcon={<CurrencyExchange />}
              sx={{ ml: 'auto', ...(sale.issue_rejected_at ? { bgcolor: '#EF4444', color: '#FFF', '&:hover': { bgcolor: '#DC2626' } } : { border: '1px solid #EF4444', color: '#EF4444' }) }}>
              Refund whole sale
            </Button>
          )}
        </Box>
      )}

      <AssignDialog open={dialog === 'assign'} sale={sale} onClose={() => setDialog(null)} />
      <CancelDialog open={dialog === 'cancel' || dialog === 'refund'} sale={sale} whole={dialog === 'refund'} voucherIds={selected}
        onClose={() => setDialog(null)} onDone={() => { setDialog(null); setSelected([]); refetch(); }} />
    </>
  );
}

function SaleDetailDrawer({ saleId, onBack, onCloseAll }) {
  return (
    <Drawer anchor="right" open={!!saleId} onClose={onCloseAll} PaperProps={drawerPaper(true)}>
      {saleId && <SaleDetailContent key={saleId} saleId={saleId} onBack={onBack} onCloseAll={onCloseAll} />}
    </Drawer>
  );
}

// ── Sales list drawer ───────────────────────────────────────────────────────
function SalesListContent({ initialQuery, onClose, onOpenSale }) {
  const [q, setQ] = useState(initialQuery);
  const [debouncedQ, setDebouncedQ] = useState(initialQuery);
  const [status, setStatus] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 350); return () => clearTimeout(t); }, [q]);

  // Server pages of 25. The page belongs to one search+filter; changing either starts again at page 1.
  const filterKey = `${debouncedQ}|${status}`;
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const goPage = (p) => setPaging({ key: filterKey, page: p });
  const params = useMemo(() => ({ q: debouncedQ, status, page, per_page: 25 }), [debouncedQ, status, page]);
  const { data, isFetching, isError, refetch } = useGetGiftVoucherSalesQuery(params);
  const sales = data?.data?.gift_voucher_sales || [];
  const totalPages = data?.data?.meta?.total_pages || 1;

  return (
    <>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: GV.bg, color: GV.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ManageSearch sx={{ fontSize: 20 }} /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1E293B' }}>Voucher sales</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>This store · newest first</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <Box sx={{ p: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.25, flexShrink: 0 }}>
          <TextField fullWidth size="small" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="GVS/26-27/00013, buyer phone or name"
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: GV.main, fontSize: 18 }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem', bgcolor: '#FFF', '&.Mui-focused fieldset': { borderColor: GV.main } } }} inputProps={{ 'aria-label': 'Search voucher sales' }} />
          <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            {FILTERS.map((f) => (
              <Chip key={f.v || 'all'} label={f.l} size="small" onClick={() => setStatus(f.v)}
                sx={{ height: 26, fontSize: '0.68rem', fontWeight: 700, borderRadius: '6px', flexShrink: 0, bgcolor: status === f.v ? '#4F46E5' : '#F1F5F9', color: status === f.v ? '#FFF' : '#64748B', '&:hover': { bgcolor: status === f.v ? '#4338CA' : '#E2E8F0' } }} />
            ))}
          </Box>
        </Box>
        <Box key={page} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {isFetching && !sales.length ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} sx={{ color: '#94A3B8' }} /></Box>
            : isError ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: 1 }}>Couldn’t load voucher sales.</Typography>
                <Button onClick={refetch} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>
              </Box>
            ) : sales.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
                <SearchOff sx={{ fontSize: 36, opacity: 0.5 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{debouncedQ || status ? 'No voucher sales match' : 'No voucher sales at this store yet'}</Typography>
              </Box>
            ) : sales.map((s) => (
              <Box key={s.id} component="button" onClick={() => onOpenSale(s.id)}
                sx={{ textAlign: 'left', font: 'inherit', color: 'inherit', width: '100%', border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.5, bgcolor: '#FFF', cursor: 'pointer', '&:hover': { borderColor: '#C7D2FE', bgcolor: '#FAFAFE' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', fontFamily: 'monospace' }}>{s.number}</Typography>
                  <StatusChip map={SALE_STATUS} value={saleStatusKey(s)} />
                </Box>
                <Typography noWrap sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.5 }}>{s.buyer_name} · {s.buyer_email}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>{s.voucher_count} voucher{s.voucher_count !== 1 ? 's' : ''} · {fmtDateTime(s.paid_at)}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800 }}>{inr(s.amount_paid)}</Typography>
                </Box>
              </Box>
            ))}
        </Box>
        {totalPages > 1 && !isError && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Button size="small" startIcon={<ChevronLeft />} disabled={page <= 1 || isFetching} onClick={() => goPage(page - 1)} sx={{ textTransform: 'none', fontWeight: 700 }}>Newer</Button>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 700 }}>Page {page} of {totalPages}</Typography>
            <Button size="small" endIcon={<ChevronRight />} disabled={page >= totalPages || isFetching} onClick={() => goPage(page + 1)} sx={{ textTransform: 'none', fontWeight: 700 }}>Older</Button>
          </Box>
        )}
    </>
  );
}

// The list stays open underneath the detail drawer, so going back keeps the search.
export default function VoucherSalesDrawer({ open, initialQuery = '', onClose }) {
  const [openId, setOpenId] = useState(null);
  const closeAll = () => { setOpenId(null); onClose(); };
  return (
    <>
      <Drawer anchor="right" open={open} onClose={closeAll} PaperProps={drawerPaper(false)}>
        {open && <SalesListContent initialQuery={initialQuery} onClose={closeAll} onOpenSale={setOpenId} />}
      </Drawer>
      <SaleDetailDrawer saleId={open ? openId : null} onBack={() => setOpenId(null)} onCloseAll={closeAll} />
    </>
  );
}
